/**
 * Workers — individual gatherer agents (not formations). State machine:
 * idle -> toNode -> harvesting -> toDropoff -> deposit, then repeat.
 * Food comes from working farms directly; timber/stone from depleting
 * resource nodes. Drop-off is the nearest active forum of the worker's team.
 *
 * Data-driven: gather_rate, carry_capacity, move_speed from units_*.json;
 * carry_capacity / haul_speed / gather_rate tech effects apply.
 */
import type { UnitDef } from "../data/types";
import type { PlacedBuilding } from "./buildings";
import type { RoadNetwork } from "./roads";
import type { Economy } from "./economy";
import type { TechEffects } from "./research";
import type { NodeKind, ResourceNode } from "./resources";

export type WorkerState =
  | "idle"
  | "toNode"
  | "harvesting"
  | "toDropoff"
  | "move"
  | "constructing"
  | "repairing";

export interface Worker {
  id: number;
  unit: UnitDef;
  team: 0 | 1;
  x: number;
  z: number;
  tx: number;
  tz: number;
  state: WorkerState;
  /** Timber/stone node, or null when working a farm. */
  node: ResourceNode | null;
  farm: PlacedBuilding | null;
  /**
   * Construction/repair target building uid, or null. Set while the worker
   * is in "constructing"/"repairing"; the render layer plays the faber
   * hammer/repair clips for these states (see render/anim.ts).
   */
  siteUid: number | null;
  carriedKind: NodeKind | "food" | null;
  carried: number;
  /** Progress through the current farm harvest cycle. */
  harvestT: number;
}

/** Data farm model: generates_resource {kind, amount, interval, hands}. */
export interface FarmResource {
  kind: string;
  amount: number;
  interval: number;
  hands: number;
}

export function farmResource(b: PlacedBuilding): FarmResource | null {
  const g = b.def.extra["generates_resource"] as
    | { kind?: string; amount?: number; interval?: number; hands?: number }
    | undefined;
  if (!g || typeof g.kind !== "string") return null;
  return {
    kind: g.kind,
    amount: typeof g.amount === "number" ? g.amount : 6,
    interval: typeof g.interval === "number" ? g.interval : 10,
    hands: typeof g.hands === "number" ? g.hands : 2,
  };
}

/** Workers currently assigned to a farm building. */
export function farmHands(farm: PlacedBuilding, workers: Worker[]): number {
  let n = 0;
  for (const w of workers) if (w.farm === farm) n++;
  return n;
}

export interface WorkforceContext {
  nodes: ResourceNode[];
  /** Active farms (infinite food sources). */
  farms: PlacedBuilding[];
  /** Active forums by team, for drop-off. */
  forums: PlacedBuilding[];
  /**
   * All buildings (both teams, any state): construction/repair targets are
   * resolved here by siteUid. The tick releases workers whose site is gone
   * or no longer needs them.
   */
  sites: PlacedBuilding[];
  economy: Economy;
  fx: TechEffects | null;
  /**
   * When true, idle workers fill free farm hands before walking to
   * resource nodes. Used by the AI director so its farm economy is
   * actually staffed (its food governor measures farm income); the
   * player's workers keep the default nodes-first behavior.
   */
  farmsFirst?: boolean;
  /**
   * Road network for the canonical `haul_speed` path bonus: hauling
   * workers move faster on road rects (data-driven, never retuned here).
   */
  roads?: RoadNetwork | null;
}

let nextWorkerId = 1;

/** Keep worker ids ahead of restored ids (save/load). */
export function reserveWorkerId(id: number): void {
  nextWorkerId = Math.max(nextWorkerId, id + 1);
}

const num = (v: unknown, fb: number): number =>
  typeof v === "number" ? v : fb;

/** Repair throughput: hp restored per worker per second while in range. */
export const REPAIR_HP_PER_SEC = 15;
/** Timber spent per hp repaired. */
export const TIMBER_PER_HP = 0.2;

export class Workforce {
  workers: Worker[] = [];

  spawn(unit: UnitDef, team: 0 | 1, x: number, z: number): Worker {
    const w: Worker = {
      id: nextWorkerId++,
      unit,
      team,
      x, z, tx: x, tz: z,
      state: "idle",
      node: null,
      farm: null,
      siteUid: null,
      carriedKind: null,
      carried: 0,
      harvestT: 0,
    };
    this.workers.push(w);
    return w;
  }

  remove(w: Worker): void {
    const i = this.workers.indexOf(w);
    if (i >= 0) this.workers.splice(i, 1);
  }

  carryCap(w: Worker, fx: TechEffects | null): number {
    return num(w.unit.extra["carry_capacity"], 10) + (fx?.carryAdd() ?? 0);
  }

  speed(w: Worker, fx: TechEffects | null): number {
    return num(w.unit.extra["move_speed"], 4.5) * (fx?.haulMultiplier() ?? 1);
  }

  gatherRate(w: Worker, fx: TechEffects | null): number {
    const kind = w.node ? w.node.kind : "food";
    return num(w.unit.extra["gather_rate"], 0.75) * (fx?.gatherMultiplier(kind) ?? 1);
  }

  /** Assign a worker to the nearest viable source, preferring the given kind. */
  autoAssign(w: Worker, ctx: WorkforceContext, prefer?: NodeKind | "food"): void {
    // farmsFirst (AI director): staff free farm hands before nodes, so the
    // AI's farm food economy — which its military governor measures — is
    // never starved of hands while workers wander to distant nodes.
    if (ctx.farmsFirst && (!prefer || prefer === "food")) {
      if (this.assignNearestFarm(w, ctx)) return;
    }
    let best: ResourceNode | null = null;
    let bestD = Infinity;
    for (const n of ctx.nodes) {
      if (n.amount <= 0) continue;
      if (prefer && prefer !== "food" && n.kind !== prefer) continue;
      const d = Math.hypot(n.x - w.x, n.z - w.z);
      if (d < bestD) {
        best = n;
        bestD = d;
      }
    }
    if (best) {
      this.assignNode(w, best);
      return;
    }
    if (!prefer || prefer === "food") {
      if (this.assignNearestFarm(w, ctx)) return;
    }
    w.state = "idle";
  }

  /** Nearest own farm with a free hand; assigns and returns true. */
  private assignNearestFarm(w: Worker, ctx: WorkforceContext): boolean {
    let bestFarm: PlacedBuilding | null = null;
    let bestD = Infinity;
    for (const f of ctx.farms) {
      if (f.team !== w.team) continue;
      const res = farmResource(f);
      if (!res || farmHands(f, this.workers) >= res.hands) continue;
      const d = Math.hypot(f.x - w.x, f.z - w.z);
      if (d < bestD) {
        bestFarm = f;
        bestD = d;
      }
    }
    if (bestFarm) {
      this.assignFarm(w, bestFarm);
      return true;
    }
    return false;
  }

  assignNode(w: Worker, node: ResourceNode): void {
    w.node = node;
    w.farm = null;
    w.state = "toNode";
    w.tx = node.x;
    w.tz = node.z;
  }

  assignFarm(w: Worker, farm: PlacedBuilding): void {
    w.node = null;
    w.farm = farm;
    w.harvestT = 0;
    w.state = "toNode";
    w.tx = farm.x;
    w.tz = farm.z;
  }

  orderMove(w: Worker, x: number, z: number): void {
    w.state = "move";
    w.tx = x;
    w.tz = z;
  }

  /**
   * Send a worker to a construction site. The worker walks there and hammers
   * until the building completes (or the site disappears). Construction
   * progress itself stays time-based — builders are the visible workforce,
   * not a gating mechanic — so this never changes build balance.
   */
  assignConstruct(w: Worker, site: PlacedBuilding): void {
    w.node = null;
    w.farm = null;
    w.siteUid = site.uid;
    w.state = "constructing";
    w.tx = site.x;
    w.tz = site.z;
  }

  /**
   * Send a worker to repair a damaged building. While in range it restores
   * hp, spending timber from the economy; released when the building is
   * whole, destroyed, or there is no timber to work with.
   */
  assignRepair(w: Worker, site: PlacedBuilding): void {
    w.node = null;
    w.farm = null;
    w.siteUid = site.uid;
    w.state = "repairing";
    w.tx = site.x;
    w.tz = site.z;
  }

  /** Clear a construction/repair assignment without reassigning. */
  releaseSite(w: Worker): void {
    w.siteUid = null;
  }

  /**
   * Assign up to `count` nearest idle workers of the team to a construction
   * site. Called when a non-instant building is placed (player blueprint or
   * AI build order); completion releases them back to autoAssign.
   */
  assignBuilders(site: PlacedBuilding, count: number, team: 0 | 1): number {
    return this.assignSiteCrew(site, count, team, (w) =>
      this.assignConstruct(w, site),
    );
  }

  /** Assign up to `count` nearest idle workers to repair a building. */
  assignRepairers(site: PlacedBuilding, count: number, team: 0 | 1): number {
    return this.assignSiteCrew(site, count, team, (w) =>
      this.assignRepair(w, site),
    );
  }

  private assignSiteCrew(
    site: PlacedBuilding,
    count: number,
    team: 0 | 1,
    assign: (w: Worker) => void,
  ): number {
    const byDist = (a: Worker, b: Worker) =>
      Math.hypot(a.x - site.x, a.z - site.z) -
      Math.hypot(b.x - site.x, b.z - site.z);
    // Idle hands first (no disruption). Starting workers auto-harvest, so
    // sites would otherwise sit builderless: fall back to the nearest
    // working hands that are not already on a site and not carrying a load.
    const idle = this.workers
      .filter((w) => w.team === team && w.state === "idle")
      .sort(byDist);
    const busy = this.workers
      .filter(
        (w) =>
          w.team === team &&
          w.state !== "idle" &&
          w.siteUid == null &&
          w.carried === 0,
      )
      .sort(byDist);
    let n = 0;
    for (const w of [...idle, ...busy]) {
      if (n >= count) break;
      assign(w);
      n++;
    }
    return n;
  }

  /** Resolve a worker's construction/repair target, or null. */
  siteOf(w: Worker, ctx: WorkforceContext): PlacedBuilding | null {
    if (w.siteUid == null) return null;
    return ctx.sites.find((b) => b.uid === w.siteUid) ?? null;
  }

  tick(dt: number, ctx: WorkforceContext): void {
    const fx = ctx.fx;
    for (const w of this.workers) {
      // Canonical haul_speed path bonus while carrying on a road.
      const roadHaul =
        w.carried > 0 ? (ctx.roads?.haulBonusAt(w.x, w.z) ?? 1) : 1;
      const speed = this.speed(w, fx) * roadHaul;
      switch (w.state) {
        case "idle":
          this.autoAssign(w, ctx);
          break;
        case "move": {
          if (this.stepToward(w, w.tx, w.tz, speed, dt)) w.state = "idle";
          break;
        }
        case "toNode": {
          const t = this.sourcePos(w);
          if (!t) {
            this.autoAssign(w, ctx);
            break;
          }
          if (this.stepToward(w, t.x, t.z, speed, dt, 2.5)) {
            w.state = "harvesting";
          }
          break;
        }
        case "harvesting": {
          // Farm hands work the data harvest cycle: every `interval`
          // seconds the farm yields `amount` of `kind`, no hauling needed.
          if (w.farm) {
            const res = farmResource(w.farm);
            if (!res || w.farm.state !== "active" || !ctx.farms.includes(w.farm)) {
              // Farm gone or destroyed: release the hand before reassigning.
              w.farm = null;
              this.autoAssign(w, ctx);
              break;
            }
            w.harvestT += dt;
            if (w.harvestT >= res.interval) {
              w.harvestT = 0;
              ctx.economy.add(res.kind, res.amount * (fx?.gatherMultiplier(res.kind) ?? 1));
            }
            break;
          }
          const src = this.liveSource(w, ctx);
          if (!src) {
            this.autoAssign(w, ctx);
            break;
          }
          const rate = this.gatherRate(w, fx);
          const cap = this.carryCap(w, fx);
          const available = src.node ? src.node.amount : Infinity;
          const take = Math.min(rate * dt, cap - w.carried, available);
          w.carried += take;
          w.carriedKind = src.kind;
          if (src.node) src.node.amount -= take;
          if (w.carried >= cap - 1e-6) {
            w.state = "toDropoff";
            const forum = nearestForum(w, ctx.forums);
            if (forum) {
              w.tx = forum.x;
              w.tz = forum.z;
            } else {
              w.state = "idle";
            }
          }
          break;
        }
        case "toDropoff": {
          if (this.stepToward(w, w.tx, w.tz, speed, dt, 3)) {
            if (w.carriedKind && w.carried > 0) {
              ctx.economy.add(w.carriedKind, w.carried);
            }
            w.carried = 0;
            w.carriedKind = null;
            // Back to the same source if it still yields.
            const src = this.liveSource(w, ctx);
            if (src) {
              w.state = "toNode";
            } else {
              this.autoAssign(w, ctx);
            }
          }
          break;
        }
        case "constructing": {
          const site = this.siteOf(w, ctx);
          if (!site || site.state !== "constructing") {
            // Done or destroyed: back to the workforce pool.
            this.releaseSite(w);
            this.autoAssign(w, ctx);
            break;
          }
          // Walk to the site, then hammer in place (the render layer plays
          // the faber hammer clips from this state). Progress is time-based;
          // the crew is the visible workforce, not a gate.
          this.stepToward(w, site.x, site.z, speed, dt, 4);
          break;
        }
        case "repairing": {
          const site = this.siteOf(w, ctx);
          if (
            !site ||
            site.state !== "active" ||
            site.hp >= site.maxHp ||
            site.team !== w.team
          ) {
            this.releaseSite(w);
            this.autoAssign(w, ctx);
            break;
          }
          if (!this.stepToward(w, site.x, site.z, speed, dt, 4)) break;
          // In range: restore hp, spending timber. No timber, no work.
          const gain = Math.min(REPAIR_HP_PER_SEC * dt, site.maxHp - site.hp);
          const cost = gain * TIMBER_PER_HP;
          const have = ctx.economy.stocks.get("timber") ?? 0;
          if (gain > 0 && have >= cost && cost > 0) {
            ctx.economy.add("timber", -cost);
            site.hp = Math.min(site.maxHp, site.hp + gain);
          }
          break;
        }
      }
    }
    // Drop depleted nodes so the sim/render layers can clean up.
    for (let i = ctx.nodes.length - 1; i >= 0; i--) {
      if (ctx.nodes[i].amount <= 0) ctx.nodes.splice(i, 1);
    }
  }

  private sourcePos(w: Worker): { x: number; z: number } | null {
    if (w.node) return { x: w.node.x, z: w.node.z };
    if (w.farm) return { x: w.farm.x, z: w.farm.z };
    return null;
  }

  /** The source if it still yields, else null. */
  private liveSource(
    w: Worker,
    ctx: WorkforceContext,
  ): { kind: NodeKind | "food"; node: ResourceNode | null } | null {
    if (w.node) {
      if (!ctx.nodes.includes(w.node) || w.node.amount <= 0) return null;
      return { kind: w.node.kind, node: w.node };
    }
    if (w.farm) {
      if (w.farm.state !== "active" || !ctx.farms.includes(w.farm)) return null;
      return { kind: "food", node: null };
    }
    return null;
  }

  /** Move toward (tx,tz); true when within `arrive` metres. */
  private stepToward(
    w: Worker,
    tx: number,
    tz: number,
    speed: number,
    dt: number,
    arrive = 0.6,
  ): boolean {
    const dx = tx - w.x;
    const dz = tz - w.z;
    const d = Math.hypot(dx, dz);
    if (d <= arrive) return true;
    const step = Math.min(d, speed * dt);
    w.x += (dx / d) * step;
    w.z += (dz / d) * step;
    return false;
  }
}

function nearestForum(w: Worker, forums: PlacedBuilding[]): PlacedBuilding | null {
  let best: PlacedBuilding | null = null;
  let bestD = Infinity;
  for (const f of forums) {
    if (f.team !== w.team || f.state !== "active") continue;
    const d = Math.hypot(f.x - w.x, f.z - w.z);
    if (d < bestD) {
      best = f;
      bestD = d;
    }
  }
  return best;
}

/** Per-second upkeep across the workforce, by resource id. */
export function workforceUpkeep(workers: Worker[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const w of workers) {
    const upkeep = (w.unit.extra["upkeep"] as Record<string, number>) ?? {};
    for (const [id, rate] of Object.entries(upkeep)) {
      out[id] = (out[id] ?? 0) + rate;
    }
  }
  return out;
}
